import { useMemo, useState } from 'react';
import { LEADER_SCOPE_LABELS, type UnitLeader } from '../types';

/** Type-to-search over the imported Trained Leaders roster. Dedupes by member
 *  id (a person can hold positions at several scopes). Used to prefill Add
 *  User and Org Chart seat assignment without retyping name + email. */
export default function LeaderSearch({
  leaders,
  placeholder = 'Search leaders by name or email…',
  districtName,
  onPick,
}: {
  leaders: UnitLeader[];
  placeholder?: string;
  /** Resolve a leader's districtId to a name for the sublabel. */
  districtName?: (districtId: string | null) => string;
  onPick: (leader: UnitLeader) => void;
}) {
  const [q, setQ] = useState('');

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const byMember = new Map<string, UnitLeader>();
    for (const l of leaders) {
      if (
        !l.fullName.toLowerCase().includes(needle) &&
        !l.email.toLowerCase().includes(needle)
      ) {
        continue;
      }
      // keep the most specific scope for a person (unit > district > council)
      const cur = byMember.get(l.memberId);
      const rank = (s: UnitLeader['scope']) => ['council', 'district', 'unit'].indexOf(s);
      if (!cur || rank(l.scope) > rank(cur.scope)) byMember.set(l.memberId, l);
    }
    return [...byMember.values()].slice(0, 8);
  }, [q, leaders]);

  const where = (l: UnitLeader) => {
    if (l.scope === 'unit') return l.unitName ?? 'a unit';
    if (l.scope === 'district') return districtName?.(l.districtId) ?? 'district';
    return 'Council';
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      {q.trim() && (
        <div className="people-dropdown">
          {matches.map((l) => (
            <button
              key={l.memberId}
              type="button"
              className="people-option"
              onClick={() => {
                onPick(l);
                setQ('');
              }}
            >
              {l.fullName}
              <span className="subtitle">
                {' '}
                — {LEADER_SCOPE_LABELS[l.scope]} · {where(l)}
                {l.email ? ` · ${l.email}` : ''}
              </span>
            </button>
          ))}
          {matches.length === 0 && <div className="people-option-empty">No leader found.</div>}
        </div>
      )}
    </div>
  );
}
